import React from 'react';
import * as echarts from 'echarts/core';
import {
    BarChart as EChartsBarChart,
} from 'echarts/charts';
import {
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    GridComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import EchartsWrapper from '@/components/echartsWrapper';

// 注册必要的组件
echarts.use([
    EChartsBarChart,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    GridComponent,
    CanvasRenderer,
]);

interface BarChartData {
    name: string;
    value: Array<[number | string, number]>; // [类别索引, 数值]
}

interface BarChartProps {
    /** 图表数据 */
    data?: BarChartData[];
    /** 图表标题 */
    title?: string;
    /** x轴标签 */
    xAxisLabel?: string;
    /** y轴标签 */
    yAxisLabel?: string;
    /** 图表样式 */
    style?: React.CSSProperties;
    /** 柱子颜色 */
    color?: string | string[];
    /** 是否显示数值标签 */
    showLabel?: boolean;
    /** 是否为分组模式 - 多个系列数据 */
    isGrouped?: boolean;
    /** 是否显示图例 */
    showLegend?: boolean;
    /** 是否为堆叠模式 - 同类别数据堆叠显示 */
    isStacked?: boolean;
    /** 柱状图最大宽度 - 可以是像素值(数字)或百分比字符串 */
    maxBarWidth?: number | string;
    /** 纵轴是否仅显示整数刻度 */
    yAxisIntegerOnly?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
    data = [{
        name: '--',
        value: [],
    }],
    title = '柱状图',
    xAxisLabel = '',
    yAxisLabel = '',
    style = { width: '100%', height: '400px' },
    color = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de'],
    showLabel = true,
    isGrouped = false,
    showLegend = true,
    isStacked = false,
    maxBarWidth,
    yAxisIntegerOnly = false
}) => {
    // 获取所有类别（x轴标签）
    const getAllCategories = () => {
        const categorySet = new Set<string | number>();
        data.forEach(series => {
            series.value.forEach(item => {
                categorySet.add(item[0]);
            });
        });
        return Array.from(categorySet);
    };

    // 根据是否为分组模式处理数据
    const categories = (isGrouped || isStacked) && data.length > 1 ?
        getAllCategories() :
        (data.length > 0 && data[0].value.length > 0 ?
            data[0].value.map((item: [number | string, number]) => item[0]) : []);

    // 生成系列数据
    const generateSeries = () => {

        console.log('BarChart categories:', categories);
        console.log('BarChart data:', data);

        if ((!isGrouped && !isStacked) || data.length === 1) {
            // 兼容原有格式 - 单系列
            const seriesData = data.length > 0 && data[0].value.length > 0 ?
                data[0].value.map((item: [number | string, number]) => item[1]) : [];

            return [
                {
                    name: data.length > 0 ? data[0].name : title,
                    type: 'bar',
                    maxBarWidth: maxBarWidth,
                    data: seriesData.map((value: number, index: number) => ({
                        value: value,
                        itemStyle: {
                            color: Array.isArray(color) ? color[index % color.length] : color,
                            borderRadius: [4, 4, 0, 0] // 圆角顶部
                        }
                    })),
                    label: {
                        show: showLabel,
                        position: 'top',
                        fontSize: 12,
                        color: '#666'
                    },
                }
            ];
        } else {
            // 分组模式或堆叠模式 - 多系列
            return data.map((series, seriesIndex) => {
                // 为每个类别创建数据映射
                const valueMap = new Map<string | number, number>();
                series.value.forEach(item => {
                    valueMap.set(item[0], item[1]);
                });

                // 按照categories顺序生成数据
                const seriesData = categories.map(category => valueMap.get(category) || 0);

                return {
                    name: series.name,
                    type: 'bar',
                    maxBarWidth: maxBarWidth,
                    stack: isStacked ? 'total' : undefined, // 堆叠模式：使用相同的stack值
                    data: seriesData.map((value: number) => ({
                        value: value,
                        itemStyle: {
                            color: Array.isArray(color) ? color[seriesIndex % color.length] : color,
                            borderRadius: isStacked ?
                                (seriesIndex === data.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]) : // 堆叠模式：只有顶部系列有圆角
                                [4, 4, 0, 0] // 分组模式：每个柱子都有圆角
                        }
                    })),
                    label: {
                        show: isStacked ? false : showLabel, // 堆叠模式：不显示数字标签
                        position: isStacked ? 'inside' : 'top', // 堆叠模式：标签显示在内部
                        fontSize: 12,
                        color: isStacked ? '#fff' : '#666',
                        formatter: isStacked ? '{c}' : undefined, // 堆叠模式：只显示数值
                    },
                };
            });
        }
    };

    const chartOption = {
        title: {
            text: title,
            left: 'center',
            textStyle: {
                fontSize: 16,
                fontWeight: 'bold'
            }
        },
        legend: {
            show: (isGrouped || isStacked) && data.length > 1 && showLegend,
            top: 'bottom',
            type: 'plain'
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            formatter: function (params: any) {
                if (Array.isArray(params)) {
                    // 分组模式或堆叠模式下的tooltip
                    let tooltip = `${params[0].name}<br/>`;
                    if (isStacked) {
                        // 堆叠模式：显示总计和各部分
                        const total = params.reduce((sum: number, param: any) => sum + param.value, 0);
                        tooltip += `总计: ${total}<br/>`;
                        params.forEach((param: any) => {
                            tooltip += `${param.seriesName}: ${param.value}<br/>`;
                        });
                    } else {
                        // 分组模式：显示各系列数据
                        params.forEach((param: any) => {
                            tooltip += `${param.seriesName}: ${param.value}<br/>`;
                        });
                    }
                    return tooltip;
                } else {
                    // 单系列模式下的tooltip
                    return `${params.seriesName}<br/>${params.name}: ${params.value}`;
                }
            }
        },
        grid: {
            left: '6%',
            right: '8%',
            bottom: (isGrouped || isStacked) && data.length > 1 && showLegend ? '15%' : '3%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            data: categories,
            name: xAxisLabel,
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: {
                rotate: categories.length > 8 ? 45 : 0, // 当数据量大时旋转标签
                fontSize: 12
            }
        },
        yAxis: {
            type: 'value',
            name: yAxisLabel,
            nameLocation: 'middle',
            nameGap: 50,
            minInterval: yAxisIntegerOnly ? 1 : undefined,
            axisLabel: {
                fontSize: 12,
                formatter: yAxisIntegerOnly
                    ? (value: number) => `${Math.round(value)}`
                    : undefined
            }
        },
        series: generateSeries()
    };

    return (
        <>
            {data && data.length > 0 ? (
                <EchartsWrapper
                    chartOption={chartOption}
                    style={style}
                />
            ) : (
                <div style={style}>
                    <h4>{title}</h4>
                    <p>暂无数据</p>
                </div>
            )}
        </>
    );
};

export default BarChart;
